import React, { useEffect, useState } from "react";
import {
  Table, Button, Modal, Form, Input, InputNumber, message, Tag,
  Dropdown, Space, Tooltip, Drawer, Divider, Switch, Row, Col, Progress, Alert,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, PercentageOutlined,
  SearchOutlined, EyeOutlined, ReloadOutlined,
  FileExcelOutlined, FilePdfOutlined, DownOutlined, UpOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  UserOutlined, TeamOutlined, CrownOutlined,
  CalendarOutlined, IdcardOutlined, FileTextOutlined,
  CopyOutlined, AppstoreOutlined, UnorderedListOutlined,
  PieChartOutlined, InfoCircleOutlined,
  ThunderboltOutlined, RocketOutlined,
} from "@ant-design/icons";
import {
  PostChargesGet, PostChargesPost,
  PostChargesUpdate, PostChargesRemove,
} from "../../../../Api/CoreApi";
import { exportToCSV } from "../../Utils/exportCSV";
import { exportToPDF } from "../../Utils/exportPDF";
import {
  formatDate, generateRefNumber, capitalize,
} from "../../Utils/formatters";
import "./PostCommission.css";

function PostCommission() {
  const [commissions, setCommissions] = useState([]);
  const [filteredCommissions, setFilteredCommissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [visitModalOpen, setVisitModalOpen] = useState(false);
  const [activeGlobalCommId, setActiveGlobalCommId] = useState(null);
  const [visitForm] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [form] = Form.useForm();

  const [totalPercentage, setTotalPercentage] = useState(0);

  // 📱 Mobile View Detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ============================================
  // FETCH DATA
  // ============================================
  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const response = await PostChargesGet();
      const arr = Array.isArray(response) ? response : [];
      setCommissions(arr);
      setFilteredCommissions(arr);
    } catch {
      message.error("Failed to fetch commissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCommissions(); }, []);

  // ============================================
  // FILTER LOGIC
  // ============================================
  useEffect(() => {
    let filtered = [...commissions];

    if (searchText) {
      filtered = filtered.filter(
        (c) =>
          c.title?.toLowerCase().includes(searchText.toLowerCase()) ||
          c.description?.toLowerCase().includes(searchText.toLowerCase()) ||
          generateRefNumber("commission", c.id).toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (statusFilter === "active") {
      filtered = filtered.filter(c => c.is_active !== false);
    } else if (statusFilter === "inactive") {
      filtered = filtered.filter(c => c.is_active === false);
    }

    setFilteredCommissions(filtered);
  }, [searchText, statusFilter, commissions]);

  // ============================================
  // PERCENTAGE CALCULATOR
  // ============================================
  const calculateTotal = (values) => {
    const admin = parseFloat(values.admin_percentage) || 0;
    const staff = parseFloat(values.staff_percentage) || 0;
    const developer = parseFloat(values.developer_percentage) || 0;
    return admin + staff + developer;
  };

  const handleFormValuesChange = (changed, all) => {
    const total = calculateTotal(all);
    setTotalPercentage(total);
  };

  const getCommissionTotal = (c) => {
    return (parseFloat(c.admin_percentage) || 0) +
      (parseFloat(c.staff_percentage) || 0) +
      (parseFloat(c.developer_percentage) || 0);
  };

  // ============================================
  // ACTIONS
  // ============================================
  const handleAdd = () => {
    setEditingItem(null);
    setTotalPercentage(0);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setTotalPercentage(getCommissionTotal(record));
    setModalOpen(true);
  };

  const handleView = (record) => {
    setSelectedItem(record);
    setDetailsDrawerOpen(true);
  };

  const handleDuplicate = (record) => {
    setEditingItem(null);
    const duplicateData = {
      ...record,
      title: `${record.title} (Copy)`,
      id: undefined,
    };
    form.setFieldsValue(duplicateData);
    setTotalPercentage(getCommissionTotal(record));
    setModalOpen(true);
    message.info("Commission duplicated. Edit and save as new.");
  };

  const handleToggleStatus = async (record) => {
    try {
      const newStatus = record.is_active === false ? true : false;
      await PostChargesUpdate(record.id, { ...record, is_active: newStatus });
      message.success(`Commission ${newStatus ? "activated" : "deactivated"}`);
      fetchCommissions();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete Commission?",
      content: "This commission structure will be permanently deleted.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await PostChargesRemove(id);
          message.success("Commission deleted successfully");
          fetchCommissions();
        } catch {
          message.error("Failed to delete");
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    const total = calculateTotal(values);

    if (total !== 100) {
      Modal.confirm({
        title: "Total Not 100%",
        content: `Current total is ${total}%. Do you still want to save?`,
        okText: "Save Anyway",
        cancelText: "Cancel",
        onOk: async () => {
          await saveCommission(values);
        },
      });
      return;
    }

    await saveCommission(values);
  };

  const saveCommission = async (values) => {
    try {
      if (editingItem) {
        await PostChargesUpdate(editingItem.id, values);
        message.success("Commission updated successfully");
      } else {
        await PostChargesPost(values);
        message.success("Commission added successfully");
      }
      setModalOpen(false);
      form.resetFields();
      setTotalPercentage(0);
      fetchCommissions();
    } catch {
      message.error("Failed to save");
    }
  };
  // ============================================
  // ✅ VISIT COMMISSION LOGIC (NAYA)
  // ============================================
  const handleOpenVisitModal = () => {
    // Backend logic ke hisaab se hum first active commission me value save karte hain
    const activeComm = commissions.find(c => c.is_active !== false);

    if (activeComm) {
      setActiveGlobalCommId(activeComm.id);
      visitForm.setFieldsValue({
        visit_commission: activeComm.visit_commission || 10
      });
      setVisitModalOpen(true);
    } else {
      message.warning("Please create at least one Active Commission first!");
    }
  };

  const handleSaveVisitCommission = async (values) => {
    if (!activeGlobalCommId) return;
    try {
      const existingRecord = commissions.find(c => c.id === activeGlobalCommId);
      // Pura data waisa hi bhejenge, bas visit_commission change karke
      const updatedData = { ...existingRecord, visit_commission: values.visit_commission };

      await PostChargesUpdate(activeGlobalCommId, updatedData);
      message.success(`Visit Commission updated to ₹${values.visit_commission} successfully!`);
      setVisitModalOpen(false);
      fetchCommissions(); // List refresh
    } catch {
      message.error("Failed to update Visit Commission");
    }
  };

  // ============================================
  // EXPORT
  // ============================================
  const exportColumns = [
    { key: "sno", label: "S.No." },
    { key: "com_ref", label: "Commission Ref", format: (r) => generateRefNumber("commission", r.id || 0) },
    { key: "title", label: "Title", format: (r) => r.title || "N/A" },
    { key: "admin_percentage", label: "Admin %", format: (r) => `${r.admin_percentage || 0}%` },
    { key: "staff_percentage", label: "Staff %", format: (r) => `${r.staff_percentage || 0}%` },
    { key: "developer_percentage", label: "Developer %", format: (r) => `${r.developer_percentage || 0}%` },
    { key: "total", label: "Total %", format: (r) => `${getCommissionTotal(r)}%` },
    { key: "plan_name", label: "Linked Plan", format: (r) => r.plan_name || "Manual" },
    { key: "description", label: "Description", format: (r) => r.description || "N/A" },
    { key: "status", label: "Status", format: (r) => (r.is_active !== false ? "Active" : "Inactive") },
    { key: "created_date", label: "Created", format: (r) => formatDate(r.created_date) },
  ];

  const handleExportCSV = (all = false) => {
    const data = all ? commissions : filteredCommissions;
    exportToCSV(data, exportColumns, "commission_structure_report");
  };

  const handleExportPDF = (all = false) => {
    const data = all ? commissions : filteredCommissions;
    exportToPDF(data, exportColumns, {
      title: "Commission Structure Report",
      subtitle: "Complete list of commission percentages",
      filename: "commission_structure_report",
    });
  };

  // ============================================
  // STATS
  // ============================================
  const stats = {
    total: commissions.length,
    active: commissions.filter(c => c.is_active !== false).length,
    inactive: commissions.filter(c => c.is_active === false).length,
    valid: commissions.filter(c => getCommissionTotal(c) === 100).length,
    auto: commissions.filter(c => c.is_auto_generated === true).length,
  };

  // ============================================
  // COLUMNS
  // ============================================
  const columns = [
    {
      title: "#", key: "sno", width: 45, fixed: "left",
      render: (_, __, i) => <span className="sno-cell">{i + 1}</span>,
    },
    {
      title: "Ref", key: "com_ref", width: 110,
      render: (_, r) => <span className="ref-cell">{generateRefNumber("commission", r.id)}</span>,
    },
    {
      title: "Title", dataIndex: "title", key: "title", width: 180,
      render: (t, r) => (
        <div>
          <span className="title-cell-text">
            <PercentageOutlined /> {t || "Untitled"}
          </span>
          {r.is_auto_generated && (
            <div>
              <span style={{
                fontSize: 10,
                color: '#3b82f6',
                fontWeight: 700,
                background: '#eff6ff',
                padding: '1px 6px',
                borderRadius: 999,
                border: '1px solid #bfdbfe',
              }}>
                ⚡ Auto | {r.plan_name}
              </span>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Admin", dataIndex: "admin_percentage", key: "admin_percentage", width: 80,
      render: (v) => (
        <Tag className="percent-tag admin">
          <CrownOutlined /> {v || 0}%
        </Tag>
      ),
    },
    {
      title: "Staff", dataIndex: "staff_percentage", key: "staff_percentage", width: 80,
      render: (v) => (
        <Tag className="percent-tag staff">
          <TeamOutlined /> {v || 0}%
        </Tag>
      ),
    },
    {
      title: "Developer", dataIndex: "developer_percentage", key: "developer_percentage", width: 90,
      render: (v) => (
        <Tag className="percent-tag user" style={{ background: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0' }}>
          <RocketOutlined /> {v || 0}%
        </Tag>
      ),
    },
    {
      title: "Total", key: "total", width: 90,
      render: (_, r) => {
        const total = getCommissionTotal(r);
        return (
          <Tag className={`total-tag ${total === 100 ? "valid" : "invalid"}`}>
            {total === 100 ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            {" "}{total}%
          </Tag>
        );
      },
    },
    {
      title: "Status", key: "status", width: 100,
      render: (_, r) => {
        const isActive = r.is_active !== false;
        return (
          <Tag className={`status-tag ${isActive ? "active" : "inactive"}`}>
            {isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            {" "}{isActive ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
    {
      title: "Actions", key: "action", width: 180, fixed: "right",
      render: (_, record) => (
        <Space size={3}>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} className="action-btn view"
              onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} className="action-btn edit"
              onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Duplicate">
            <Button size="small" icon={<CopyOutlined />} className="action-btn duplicate"
              onClick={() => handleDuplicate(record)} />
          </Tooltip>
          <Tooltip title={record.is_active !== false ? "Deactivate" : "Activate"}>
            <Button
              size="small"
              icon={record.is_active !== false ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              className={`action-btn ${record.is_active !== false ? "deactivate" : "activate"}`}
              onClick={() => handleToggleStatus(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" icon={<DeleteOutlined />} className="action-btn delete"
              onClick={() => handleDelete(record.id)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const csvMenuItems = {
    items: [
      { key: "1", label: `Export Filtered (${filteredCommissions.length})`, onClick: () => handleExportCSV(false) },
      { key: "2", label: `Export All (${commissions.length})`, onClick: () => handleExportCSV(true) },
    ],
  };

  const pdfMenuItems = {
    items: [
      { key: "1", label: `Export Filtered (${filteredCommissions.length})`, onClick: () => handleExportPDF(false) },
      { key: "2", label: `Export All (${commissions.length})`, onClick: () => handleExportPDF(true) },
    ],
  };

  // ============================================
  // ✅ SHARED CARD RENDERER (Used by Grid & Mobile)
  // ============================================
  const renderCommissionCard = (c) => {
    const total = getCommissionTotal(c);
    const isValid = total === 100;

    return (
      <div key={c.id} className={`commission-premium-card ${c.is_active === false ? "inactive-card" : ""}`}>
        <div className="card-header">
          <div className="card-title-block">
            <div className="card-icon"><PercentageOutlined /></div>
            <div>
              <h4>{c.title || "Untitled Commission"}</h4>
              <span className="card-ref">{generateRefNumber("commission", c.id)}</span>
            </div>
          </div>
          <Tag className={`status-tag ${c.is_active !== false ? "active" : "inactive"}`}>
            {c.is_active !== false ? "● Active" : "○ Inactive"}
          </Tag>
        </div>

        {c.is_auto_generated && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, marginBottom: 10,
          }}>
            <ThunderboltOutlined style={{ color: '#3b82f6', fontSize: 12 }} />
            <span style={{ fontSize: 11, color: '#3b82f6', fontWeight: 700 }}>Auto-Generated</span>
            <span style={{
              fontSize: 10, color: '#64748b', marginLeft: 'auto',
              background: '#fff', padding: '1px 6px', borderRadius: 999, border: '1px solid #e2e8f0',
            }}>
              Plan: {c.plan_name || 'N/A'}
            </span>
          </div>
        )}

        {c.plan_type && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px',
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 999,
            fontSize: 10, color: '#64748b', fontWeight: 600, marginBottom: 10,
          }}>
            <RocketOutlined /> {c.plan_type?.replace(/_/g, ' ').toUpperCase()}
            {c.plan_price && <span style={{ color: '#10b981', marginLeft: 4 }}>₹{c.plan_price}</span>}
          </div>
        )}

        <div className="total-percentage-block">
          <div className="total-header">
            <span>Total Distribution</span>
            <span className={`total-value ${isValid ? "valid" : "invalid"}`}>{total}%</span>
          </div>
          <Progress
            percent={total > 100 ? 100 : total}
            strokeColor={isValid ? "#52c41a" : total > 100 ? "#f5222d" : "#faad14"}
            showInfo={false} size="small"
          />
          {!isValid && (
            <span className="total-warning">
              {total > 100 ? `⚠️ Exceeds by ${total - 100}%` : `⚠️ Short by ${100 - total}%`}
            </span>
          )}
        </div>

        <div className="distribution-grid three-cols">
          <div className="dist-item admin">
            <div className="dist-icon"><CrownOutlined /></div>
            <div className="dist-content">
              <span className="dist-label">Admin</span>
              <strong className="dist-value">{c.admin_percentage || 0}%</strong>
            </div>
          </div>
          <div className="dist-item staff">
            <div className="dist-icon"><TeamOutlined /></div>
            <div className="dist-content">
              <span className="dist-label">Staff</span>
              <strong className="dist-value">{c.staff_percentage || 0}%</strong>
            </div>
          </div>
          <div className="dist-item" style={{
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            border: '1.5px solid #bbf7d0', borderRadius: 10, padding: '8px 10px',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <div className="dist-icon" style={{ color: '#16a34a' }}><RocketOutlined /></div>
            <div className="dist-content">
              <span className="dist-label">Developer</span>
              <strong className="dist-value" style={{ color: '#16a34a' }}>
                {c.developer_percentage || 0}%
              </strong>
            </div>
          </div>
        </div>

        {c.is_auto_generated && (
          <div style={{
            marginTop: 10, padding: '8px 10px', background: '#fafafa',
            border: '1px solid #f0f0f0', borderRadius: 8, fontSize: 11, color: '#64748b',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4, color: '#475569' }}>📋 Commission Rules:</div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>👤 Staff Added User:</span>
              <span style={{ fontWeight: 600 }}>
                Admin {c.admin_percentage || 70}% | Staff {c.staff_percentage || 15}% | Dev {c.developer_percentage || 15}%
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
              <span>🌐 Self Register:</span>
              <span style={{ fontWeight: 600 }}>
                Admin {c.self_admin_percentage || 85}% | Dev {c.self_developer_percentage || 15}%
              </span>
            </div>
          </div>
        )}

        {c.description && (
          <div className="card-description">
            <FileTextOutlined /> {c.description.slice(0, 80)}
            {c.description.length > 80 && "..."}
          </div>
        )}

        <div className="card-actions">
          <Button size="small" icon={<EyeOutlined />} className="premium-btn view"
            onClick={() => handleView(c)}>View</Button>
          <Button size="small" icon={<EditOutlined />} className="premium-btn edit"
            onClick={() => handleEdit(c)}>Edit</Button>
          <Button size="small" icon={<CopyOutlined />} className="premium-btn duplicate"
            onClick={() => handleDuplicate(c)}>Copy</Button>
        </div>

        <div className="card-toggle-section">
          <span>Commission Status</span>
          <Switch checked={c.is_active !== false} onChange={() => handleToggleStatus(c)}
            checkedChildren="Active" unCheckedChildren="Inactive" size="small" />
        </div>
      </div>
    );
  };

  return (
    <div className="commission-page">

      {/* ============ HEADER + STATS ============ */}
      <div className="header-stats-wrapper">
        <div className="page-header">
          <div className="header-left">
            <div className="title-row">
              <div className="title-icon"><PercentageOutlined /></div>
              <div className="title-content">
                <h2 className="page-title">Commission Structure</h2>
                <p className="page-subtitle">
                  {filteredCommissions.length} of {commissions.length} commission types
                </p>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchCommissions}
                loading={loading}
                className="icon-btn icon-mobile-btn"
              />
            </Tooltip>

            <Dropdown menu={csvMenuItems}>
              <Button icon={<FileExcelOutlined />} className="csv-btn icon-mobile-btn">
                <span className="hide-on-mobile">CSV</span>
              </Button>
            </Dropdown>

            <Dropdown menu={pdfMenuItems}>
              <Button icon={<FilePdfOutlined />} className="pdf-btn icon-mobile-btn">
                <span className="hide-on-mobile">PDF</span>
              </Button>
            </Dropdown>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="add-btn icon-mobile-btn"
              onClick={handleAdd}
            >
              <span className="hide-on-mobile">Add Commission</span>
            </Button>

            {isMobile && (
              <Button
                className="toggle-stats-btn icon-mobile-btn"
                onClick={() => setShowStats(!showStats)}
                icon={showStats ? <UpOutlined /> : <DownOutlined />}
              />
            )}
          </div>
        </div>

        <div className={`mini-stats-bar ${!isMobile || showStats ? 'show' : 'hide'}`}>
          <div className="mini-stat total">
            <PercentageOutlined /> <b>{stats.total}</b> <span>Total</span>
          </div>
          <div className="mini-divider" />
          <div className="mini-stat active">
            <CheckCircleOutlined /> <b>{stats.active}</b> <span>Active</span>
          </div>
          <div className="mini-divider" />
          <div className="mini-stat inactive">
            <CloseCircleOutlined /> <b>{stats.inactive}</b> <span>Inactive</span>
          </div>
          <div className="mini-divider" />
          <div className="mini-stat valid">
            <PieChartOutlined /> <b>{stats.valid}</b> <span>Valid (100%)</span>
          </div>
          <div className="mini-divider" />
          <div className="mini-stat" style={{ color: '#3b82f6' }}>
            <ThunderboltOutlined /> <b>{stats.auto}</b> <span>Auto-Generated</span>
          </div>
        </div>
      </div>

      {/* ============ QUICK FILTERS ============ */}
      <div className="quick-filters-bar">
        <div className="filter-tabs">
          <button className={`filter-tab ${statusFilter === "all" ? "active" : ""}`}
            onClick={() => setStatusFilter("all")}>
            <PercentageOutlined /> All <span className="tab-count">{stats.total}</span>
          </button>
          <button className={`filter-tab ${statusFilter === "active" ? "active" : ""}`}
            onClick={() => setStatusFilter("active")}>
            <CheckCircleOutlined /> Active <span className="tab-count">{stats.active}</span>
          </button>
          <button className={`filter-tab ${statusFilter === "inactive" ? "active" : ""}`}
            onClick={() => setStatusFilter("inactive")}>
            <CloseCircleOutlined /> Inactive <span className="tab-count">{stats.inactive}</span>
          </button>
        </div>

        <div className="toolbar-right">
          <Input placeholder="Search by title, ref..." prefix={<SearchOutlined />}
            value={searchText} onChange={(e) => setSearchText(e.target.value)}
            className="toolbar-search" allowClear />

          {/* 👇 NAYA BUTTON AUR GRID/LIST TOGGLE 👇 */}
          <div className="view-toggle hide-on-mobile" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>

            {/* 🔥 Naya Visit Commission Button */}
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={handleOpenVisitModal}
              style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6', borderRadius: '6px' }}
            >
              Visit Commission
            </Button>

            {/* Purana Grid/List button */}
            <div style={{ display: 'flex', background: '#f8fafc', padding: '2px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
              <button className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")} style={{ margin: 0, border: 'none', background: viewMode === 'grid' ? '#fff' : 'transparent', boxShadow: viewMode === 'grid' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none' }}>
                <AppstoreOutlined /> Grid
              </button>
              <button className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")} style={{ margin: 0, border: 'none', background: viewMode === 'list' ? '#fff' : 'transparent', boxShadow: viewMode === 'list' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none' }}>
                <UnorderedListOutlined /> List
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* ============ 📱 MOBILE VIEW = Always Cards ============ */}
      {isMobile && filteredCommissions.length > 0 && (
        <div className="commission-cards-grid">
          {filteredCommissions.map((c) => renderCommissionCard(c))}
        </div>
      )}

      {/* ============ 🖥️ DESKTOP GRID VIEW ============ */}
      {!isMobile && viewMode === "grid" && filteredCommissions.length > 0 && (
        <div className="commission-cards-grid">
          {filteredCommissions.map((c) => renderCommissionCard(c))}
        </div>
      )}

      {/* ============ 🖥️ DESKTOP LIST VIEW (TABLE) ============ */}
      {!isMobile && viewMode === "list" && (
        <div className="table-card">
          <Table rowKey="id" columns={columns} dataSource={filteredCommissions}
            loading={loading} size="small"
            pagination={{
              pageSize: 10, showSizeChanger: true, size: "small",
              showTotal: (total) => `Total ${total}`
            }}
            scroll={{ x: 1100 }} />
        </div>
      )}

      {/* ============ EMPTY STATE ============ */}
      {filteredCommissions.length === 0 && !loading && (
        <div className="empty-state-card">
          <PercentageOutlined className="empty-icon" />
          <h3>No Commission Structures Found</h3>
          <p>Add your first commission structure to get started</p>
          <Button type="primary" icon={<PlusOutlined />} className="add-btn" onClick={handleAdd}>
            Add First Commission
          </Button>
        </div>
      )}
  
      {/* ============ ADD/EDIT MODAL ============ */}
      
      <Modal
        title={
          <div className="modal-title">
            {editingItem ? <EditOutlined /> : <PlusOutlined />}
            {editingItem ? " Edit Commission" : " Add Commission"}
          </div>
        }
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); setTotalPercentage(0); }}
        footer={null} width={isMobile ? '100%' : 600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}
          onValuesChange={handleFormValuesChange} size="middle">

          <Form.Item name="title" label="Commission Title"
            rules={[{ required: true, message: "Please enter title" }]}>
            <Input placeholder="e.g., Recharge Commission, Referral Bonus" />
          </Form.Item>

          <div className={`total-indicator ${totalPercentage === 100 ? "valid" :
            totalPercentage > 100 ? "over" : "under"
            }`}>
            <div className="indicator-header">
              <span><PieChartOutlined /> Total Distribution</span>
              <strong>{totalPercentage}%</strong>
            </div>
            <Progress
              percent={totalPercentage > 100 ? 100 : totalPercentage}
              strokeColor={
                totalPercentage === 100 ? "#52c41a" :
                  totalPercentage > 100 ? "#f5222d" : "#faad14"
              }
              showInfo={false} size="small"
            />
            <div className="indicator-message">
              {totalPercentage === 100 && "✅ Perfect distribution!"}
              {totalPercentage > 100 && `⚠️ Exceeds by ${totalPercentage - 100}%`}
              {totalPercentage < 100 && totalPercentage > 0 && `⚠️ Need ${100 - totalPercentage}% more`}
              {totalPercentage === 0 && "Enter percentages below"}
            </div>
          </div>

          <Row gutter={12}>
            <Col span={isMobile ? 24 : 8}>
              <Form.Item name="admin_percentage" label={<span><CrownOutlined /> Admin %</span>}
                rules={[{ required: true, message: "Required" }]}>
                <InputNumber min={0} max={100} style={{ width: "100%" }} placeholder="0-100" />
              </Form.Item>
            </Col>
            <Col span={isMobile ? 24 : 8}>
              <Form.Item name="staff_percentage" label={<span><TeamOutlined /> Staff %</span>}
                rules={[{ required: true, message: "Required" }]}>
                <InputNumber min={0} max={100} style={{ width: "100%" }} placeholder="0-100" />
              </Form.Item>
            </Col>
            <Col span={isMobile ? 24 : 8}>
              <Form.Item
                name="developer_percentage"
                label={<span><RocketOutlined style={{ color: '#16a34a' }} /> Developer %</span>}
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber min={0} max={100} style={{ width: "100%" }} placeholder="0-100" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Describe the commission structure..." showCount maxLength={300} />
          </Form.Item>

          <Form.Item name="is_active" label="Status" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" defaultChecked />
          </Form.Item>

          <div className="modal-actions">
            <Button onClick={() => { setModalOpen(false); form.resetFields(); setTotalPercentage(0); }}>Cancel</Button>
            <Button onClick={() => { form.resetFields(); setTotalPercentage(0); }}>Reset</Button>
            <Button type="primary" htmlType="submit" className="save-btn">
              {editingItem ? "Update" : "Add"} Commission
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ============ ✅ VISIT COMMISSION MODAL (NAYA) ============ */}
      <Modal
        title={
          <div className="modal-title" style={{ color: '#6d28d9' }}>
            <EyeOutlined style={{ marginRight: 8 }} /> Global Visit Commission
          </div>
        }
        open={visitModalOpen}
        onCancel={() => setVisitModalOpen(false)}
        footer={null}
        width={400}
      >
        <Alert
          message="How it works"
          description="This amount (₹) will be given to the Staff when a user they referred gets a profile view."
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />

        <Form form={visitForm} layout="vertical" onFinish={handleSaveVisitCommission}>
          <Form.Item
            name="visit_commission"
            label="Visit Commission Amount (₹)"
            rules={[{ required: true, message: "Please enter amount!" }]}
          >
            <InputNumber
              min={0}
              prefix="₹"
              style={{ width: "100%" }}
              size="large"
              placeholder="e.g. 10"
            />
          </Form.Item>

          <div className="modal-actions" style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button onClick={() => setVisitModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6' }}>
              Save Amount
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ============ DETAILS DRAWER ============ */}
      <Drawer
        title={<div className="drawer-title"><PercentageOutlined /> Commission Details</div>}
        placement="right" width={isMobile ? '100%' : 460}
        onClose={() => setDetailsDrawerOpen(false)} open={detailsDrawerOpen}
      >
        {selectedItem && (
          <div className="commission-details">
            <div className="detail-header">
              <div className="detail-icon-big"><PercentageOutlined /></div>
              <h2>{selectedItem.title || "Untitled Commission"}</h2>
              <div className="detail-ref-tag">{generateRefNumber("commission", selectedItem.id)}</div>

              {selectedItem.is_auto_generated && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px',
                  background: '#eff6ff', color: '#3b82f6', border: '1.5px solid #bfdbfe',
                  borderRadius: 999, fontSize: 11, fontWeight: 700, margin: '6px auto',
                }}>
                  <ThunderboltOutlined /> Auto-Generated | Plan: {selectedItem.plan_name}
                </div>
              )}

              <div className="detail-tags">
                <Tag className={`status-tag ${selectedItem.is_active !== false ? "active" : "inactive"}`}>
                  {selectedItem.is_active !== false ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  {" "}{selectedItem.is_active !== false ? "Active" : "Inactive"}
                </Tag>
                <Tag className={`total-tag ${getCommissionTotal(selectedItem) === 100 ? "valid" : "invalid"}`}>
                  Total: {getCommissionTotal(selectedItem)}%
                </Tag>
              </div>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <div className="detail-section">
              <h4><PieChartOutlined /> Distribution Breakdown</h4>
              <div className="dist-detail-list">
                <div className="dist-detail-item admin">
                  <div className="dist-detail-header">
                    <CrownOutlined /> <span>Admin</span>
                    <strong>{selectedItem.admin_percentage || 0}%</strong>
                  </div>
                  <Progress percent={parseFloat(selectedItem.admin_percentage) || 0}
                    strokeColor="#ff0055" showInfo={false} size="small" />
                </div>
                <div className="dist-detail-item staff">
                  <div className="dist-detail-header">
                    <TeamOutlined /> <span>Staff</span>
                    <strong>{selectedItem.staff_percentage || 0}%</strong>
                  </div>
                  <Progress percent={parseFloat(selectedItem.staff_percentage) || 0}
                    strokeColor="#1890ff" showInfo={false} size="small" />
                </div>
                <div className="dist-detail-item" style={{ border: '1px solid #bbf7d0', borderRadius: 8, padding: 8 }}>
                  <div className="dist-detail-header">
                    <RocketOutlined style={{ color: '#16a34a' }} /> <span>Developer</span>
                    <strong style={{ color: '#16a34a' }}>{selectedItem.developer_percentage || 0}%</strong>
                  </div>
                  <Progress percent={parseFloat(selectedItem.developer_percentage) || 0}
                    strokeColor="#16a34a" showInfo={false} size="small" />
                </div>
              </div>

              {getCommissionTotal(selectedItem) !== 100 && (
                <Alert
                  message={
                    getCommissionTotal(selectedItem) > 100
                      ? `Distribution exceeds by ${getCommissionTotal(selectedItem) - 100}%`
                      : `Distribution is short by ${100 - getCommissionTotal(selectedItem)}%`
                  }
                  type="warning" showIcon className="dist-alert"
                />
              )}
            </div>

            {selectedItem.is_auto_generated && (
              <>
                <Divider style={{ margin: "12px 0" }} />
                <div className="detail-section">
                  <h4><RocketOutlined /> Commission Rules</h4>
                  <div style={{
                    background: '#f0fdf4', border: '1px solid #bbf7d0',
                    borderRadius: 8, padding: '10px 12px', marginBottom: 8,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#166534', marginBottom: 6 }}>
                      👤 When Staff Adds User:
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Tag color="red">Admin: {selectedItem.admin_percentage || 70}%</Tag>
                      <Tag color="blue">Staff: {selectedItem.staff_percentage || 15}%</Tag>
                      <Tag color="green">Developer: {selectedItem.developer_percentage || 15}%</Tag>
                    </div>
                  </div>
                  <div style={{
                    background: '#eff6ff', border: '1px solid #bfdbfe',
                    borderRadius: 8, padding: '10px 12px',
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>
                      🌐 When User Self Registers:
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Tag color="red">Admin: {selectedItem.self_admin_percentage || 85}%</Tag>
                      <Tag color="blue">Staff: 0%</Tag>
                      <Tag color="green">Developer: {selectedItem.self_developer_percentage || 15}%</Tag>
                    </div>
                  </div>
                </div>
              </>
            )}

            <Divider style={{ margin: "12px 0" }} />

            {selectedItem.description && (
              <>
                <div className="detail-section">
                  <h4><FileTextOutlined /> Description</h4>
                  <div className="detail-description">{selectedItem.description}</div>
                </div>
                <Divider style={{ margin: "12px 0" }} />
              </>
            )}

            <div className="detail-section">
              <h4><InfoCircleOutlined /> Information</h4>
              <div className="detail-item">
                <IdcardOutlined /><strong>Ref ID</strong>
                <span className="ref-highlight">{generateRefNumber("commission", selectedItem.id)}</span>
              </div>
              {selectedItem.plan_name && (
                <div className="detail-item">
                  <RocketOutlined /><strong>Linked Plan</strong>
                  <span style={{ color: '#3b82f6', fontWeight: 600 }}>{selectedItem.plan_name}</span>
                </div>
              )}
              {selectedItem.plan_price && (
                <div className="detail-item">
                  <CrownOutlined /><strong>Plan Price</strong>
                  <span style={{ color: '#10b981', fontWeight: 600 }}>₹{selectedItem.plan_price}</span>
                </div>
              )}
              <div className="detail-item">
                <CalendarOutlined /><strong>Created</strong>
                <span>{formatDate(selectedItem.created_date)}</span>
              </div>
              <div className="detail-item">
                <PieChartOutlined /><strong>Total</strong>
                <span className={getCommissionTotal(selectedItem) === 100 ? "valid-text" : "invalid-text"}>
                  {getCommissionTotal(selectedItem)}%
                </span>
              </div>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <div className="drawer-actions">
              <Button icon={<EditOutlined />} className="drawer-btn edit"
                onClick={() => { setDetailsDrawerOpen(false); handleEdit(selectedItem); }}>Edit</Button>
              <Button icon={<CopyOutlined />} className="drawer-btn duplicate"
                onClick={() => { setDetailsDrawerOpen(false); handleDuplicate(selectedItem); }}>Duplicate</Button>
              <Button
                icon={selectedItem.is_active !== false ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                className={`drawer-btn ${selectedItem.is_active !== false ? "deactivate" : "activate"}`}
                onClick={() => { handleToggleStatus(selectedItem); setDetailsDrawerOpen(false); }}>
                {selectedItem.is_active !== false ? "Deactivate" : "Activate"}
              </Button>
              <Button danger icon={<DeleteOutlined />}
                onClick={() => { setDetailsDrawerOpen(false); handleDelete(selectedItem.id); }}>Delete</Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default PostCommission;